import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Check, Minus, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const d = (s) => (s ? String(s).slice(0, 10) : '—');

const Receipt = ({ r }) => {
  if (!r?.applicable) return <span className="text-muted-foreground" title="only the auto purge sends an erasure receipt"><Minus className="w-4 h-4 inline" /></span>;
  return r.sent ? <span className="text-green-700" title={r.at || ''}><Check className="w-4 h-4 inline" /> sent</span> : <span className="text-red-700">not sent</span>;
};

const ProofCell = ({ token, accountId, testId }) => {
  const [busy, setBusy] = useState(false);
  const [rep, setRep] = useState(null);
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/deletion-proof`, { account_id: accountId }, { headers: { Authorization: `Bearer ${token}` } });
      setRep(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Deletion proof failed'); }
    finally { setBusy(false); }
  };
  const tone = rep?.verdict === 'CLEAN' ? 'text-green-700' : rep?.verdict === 'HELD' ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="flex flex-col gap-1">
      <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" disabled={busy} onClick={run} data-testid={`${testId}-proof-run`}>{busy ? 'Checking…' : 'Run Deletion proof'}</Button>
      {rep && <span className={`text-xs font-semibold ${tone}`} data-testid={`${testId}-proof-verdict`}>{rep.verdict} · {rep.left_behind.length} left behind · {(rep.held_pending_legal_advice || []).length} held · {rep.expected_audit_rows.length} expected</span>}
    </div>
  );
};

export const DeletedAccountsPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/deleted-accounts`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load deleted accounts'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const rec = data?.reconciliation;
  return (
    <div className="bg-card rounded-2xl border border-border/50 shadow-sm p-5 mb-6" data-testid="deleted-accounts-panel">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div>
          <h3 className="font-bold text-lg">Deleted Users (read-only)</h3>
          <p className="text-xs text-muted-foreground">One row per account pending deletion, purged or merged. Account id and em: reference only — no address, no name. Rows hide after {data?.display_window_days ?? 30} days (display only; records stay).</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={busy} data-testid="deleted-accounts-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh</Button>
      </div>
      {data && (
        <>
          <div className="rounded-lg border px-3 py-2 text-xs mb-3 bg-muted/30" data-testid="deleted-accounts-pending">
            <span className="font-semibold">In the 30-day pending-deletion period now: {data.pending_deletion_period.length}</span>
            {data.pending_deletion_period.length > 0 && (
              <ul className="list-disc pl-5 mt-1">{data.pending_deletion_period.map((p) => <li key={p.account_id_prefix} className="font-mono">{p.account_id_prefix} · {p.account_ref} · deleted {d(p.deleted_at)} · purge due {d(p.purge_due)}{p.lockout_deferrals ? ` · deferred ${p.lockout_deferrals}x (lockout)` : ''} · {p.account_class}</li>)}</ul>
            )}
            {data.admin_soft_deleted_not_scheduled.length > 0 && <p className="mt-1 text-muted-foreground">Admin soft-deleted, not auto-purged: {data.admin_soft_deleted_not_scheduled.map((p) => p.account_id_prefix).join(', ')}</p>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="deleted-accounts-table">
              <thead><tr className="text-left border-b border-border/60"><th className="py-1 pr-2">Account</th><th className="py-1 pr-2">Status</th><th className="py-1 pr-2">Deleted</th><th className="py-1 pr-2">Purged</th><th className="py-1 pr-2">How</th><th className="py-1 pr-2">By whom</th><th className="py-1 pr-2">Receipt</th><th className="py-1">Proof</th></tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={8} className="py-3 text-muted-foreground">No account deleted, purged or merged inside the window.</td></tr>}
                {data.rows.map((r) => {
                  const tid = `deleted-account-${(r.account_id || '?').slice(0, 8)}`;
                  return (
                    <tr key={`${r.evidence.collection}-${r.evidence.id_prefix}`} className="border-b border-border/30 align-top" data-testid={tid}>
                      <td className="py-1 pr-2 font-mono">{(r.account_id || '?').slice(0, 8)}<br /><span className="text-muted-foreground">{r.account_ref || '—'} · {r.account_class}</span></td>
                      <td className="py-1 pr-2">{r.status}</td>
                      <td className="py-1 pr-2 font-mono">{d(r.deleted_at)}</td>
                      <td className="py-1 pr-2 font-mono">{d(r.purged_at)}</td>
                      <td className="py-1 pr-2">{r.how}</td>
                      <td className="py-1 pr-2 font-mono">{r.by}</td>
                      <td className="py-1 pr-2"><Receipt r={r.erasure_receipt} /></td>
                      <td className="py-1"><ProofCell token={token} accountId={r.account_id} testId={tid} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rec && (
            <details className="mt-3 text-xs" data-testid="deleted-accounts-reconciliation">
              <summary className="cursor-pointer font-semibold">Account count since {rec.since}: {rec.accounts_now_total} now ({rec.accounts_now_active} active) · implied {rec.implied_total_at_since} on {rec.since}</summary>
              <p className="mt-1">Signups since (still present): {rec.signups_since_still_present} · purged since: {rec.purged_since} ({Object.entries(rec.purged_since_by_class).map(([k, v]) => `${k} ${v}`).join(', ') || '—'}; {rec.purged_since_created_after_since} created after {rec.since}, {rec.purged_since_creation_date_unknown} with unknown creation date) · merged since: {rec.merged_since}</p>
              <p className="mt-1 text-muted-foreground">{rec.formula}</p>
            </details>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">Compares: {data.compares}</p>
        </>
      )}
    </div>
  );
};
